import React, { useEffect } from 'react';
import { isEmpty } from 'lodash';
import { Redirect } from '@reach/router';

import Loading from '../../components/Loading';
import { useBuild, useJsonApi } from '../../services/GlobalStore/GlobalStore';
import routes from '../../messages/routes';
import useAuthenticate from '../../hooks/useAuthenticate';
import { useLocalStorageState } from '../../hooks/useLocalStorageState';
import { getIdFromNumber } from '../ClaimPage/Claim';
import { PassengerModel } from '../../models/PassengerModel';
import { steps, translateRoute } from '../../utils/routes';
import { isBrowser } from '../../utils/utils';

export enum PageType {
  Claim = 'shareableClaimPage',
  Passenger = 'shareablePassengerPage',
  Funnel = 'funnel',
  Account = 'account',
  Payment = 'shareablePaymentPage',
}

/**
 * Sorts passengers by MainBooker, Minor, id ASC
 * @param a
 * @param b
 */
export const sortPassengers = (a: PassengerModel, b: PassengerModel) => {
  if (a.main_booker === true) return -1;
  if (b.main_booker === true) return 1;
  if (a.person.is_minor && !b.person.is_minor) return -1;
  if (!a.person.is_minor && b.person.is_minor) return 1;
  return Number(a.id) - Number(b.id);
};

const PassengerContainer = ({
  passengerId,
  claimNumber,
  children,
  redirect = true,
}: {
  passengerId?: string;
  claimNumber: string;
  children?: JSX.Element;
  redirect?: true;
}): any => {
  const claimId = claimNumber && getIdFromNumber(claimNumber);
  const { token } = useAuthenticate();
  const [, setPassengersIds, getPassengersIds] = useLocalStorageState(
    `passengersIds-${claimId}`
  );

  // Get passenger
  const { data, loading, get } = useJsonApi(
    passengerId
      ? `/passengers/${passengerId}`
      : `/claims/${claimId}/passengers`,
    {
      queryParams: {
        include: ['person.country'].join(),
      },
      cachePolicy: 'no-cache',
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  useEffect(() => {
    if (isEmpty(data) && !loading) {
      get();
    }
    // eslint-disable-next-line
  }, [token]);

  /**
   * Here we setup passenger order that we'll navigate on
   * First main booker, then minors
   */
  useEffect(() => {
    if (!passengerId && Array.isArray(data) && data?.length) {
      const sortedPassengers = data
        .sort(sortPassengers)
        .filter(
          passenger =>
            passenger?.person?.is_minor === true ||
            passenger?.main_booker === true
        )
        .map((passenger: PassengerModel) => passenger.id);
      setPassengersIds(sortedPassengers);
    }
    // eslint-disable-next-line
  }, [loading]);

  // Get claim
  const claim = useBuild(
    'claims',
    claimId || (isBrowser() && localStorage.getItem('yo:claimId'))
  );

  useEffect(() => {
    if (
      !localStorage.getItem('yo:claimId') ||
      localStorage.getItem('yo:claimId') === 'undefined'
    )
      localStorage.setItem('yo:claimId', claimId);
    // eslint-disable-next-line
  }, [claim?.id]);

  if (loading) {
    return (
      <div className="flex justify-center mt-40">
        <Loading />
      </div>
    );
  }

  if (
    redirect &&
    !loading &&
    !passengerId &&
    !isEmpty(data) &&
    !isEmpty(claim) &&
    getPassengersIds()?.length
  ) {
    const nextPath = [
      '',
      translateRoute(routes[steps.CongratulationsBasepath]),
      claim.claim_number,
      translateRoute(routes[steps.Passenger]),
      getPassengersIds()?.[0],
    ];
    return (
      <Redirect
        to={nextPath.join('/')}
        state={{
          passengerIndex: 0,
        }}
        noThrow={true}
      />
    );
  }

  if (!redirect && data && claim && !loading) {
    return children;
  }

  return null;
};

export default PassengerContainer;
